
import * as React from "react";
import { format, isValid, addDays, differenceInDays, parseISO, eachDayOfInterval, isSameDay, isWithinInterval } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { SiteBooking } from "@/hooks/useSiteBookings";

interface DateRangePickerProps {
  startDate: Date | undefined;
  endDate: Date | undefined;
  setStartDate: (date: Date | undefined) => void;
  setEndDate: (date: Date | undefined) => void;
  startLabel?: string;
  endLabel?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  disablePastDates?: boolean;
  displayUnit?: "days" | "nights"; // Display unit for the range (days is inclusive, nights is exclusive)
  showRangeCount?: boolean; // Whether to display "(N nights/days)" suffix in the trigger
  bookings?: SiteBooking[]; // Existing paid bookings to disable on the calendar
  bookingsLoading?: boolean;
  onInvalidRange?: (message: string) => void;
  numberOfMonths?: number;
  stayPresets?: Array<{ label: string; nights: number }>; // Quick stay length buttons
}

export function DateRangePicker({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  startLabel = "First night of camping",
  endLabel = "Last night of camping",
  className,
  placeholder = "Select date range",
  disabled = false,
  disablePastDates = true, // Default to true to disable past dates
  displayUnit = "nights", // Default to nights for backward compatibility
  showRangeCount = true,
  bookings = [],
  bookingsLoading = false,
  onInvalidRange,
  numberOfMonths = 1,
  stayPresets,
}: DateRangePickerProps) {
  const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);
  const isMobile = useIsMobile();

  // Get today's date at midnight for consistent comparison
  const today = React.useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  // Compute booked nights from existing reservations.
  // Same-day turnover: end_date itself is checkout day and remains selectable.
  // We block start_date through end_date - 1.
  const bookedNights = React.useMemo(() => {
    const set = new Set<string>();
    for (const b of bookings) {
      try {
        const start = parseISO(b.start_date);
        const end = parseISO(b.end_date);
        if (!isValid(start) || !isValid(end)) continue;
        const lastNight = addDays(end, -1);
        if (lastNight < start) {
          set.add(format(start, "yyyy-MM-dd"));
          continue;
        }
        for (const d of eachDayOfInterval({ start, end: lastNight })) {
          set.add(format(d, "yyyy-MM-dd"));
        }
      } catch (e) {
        console.warn("[DateRangePicker] bad booking dates", b, e);
      }
    }
    return set;
  }, [bookings]);

  const isDateBooked = React.useCallback(
    (date: Date) => bookedNights.has(format(date, "yyyy-MM-dd")),
    [bookedNights]
  );

  const rangeOverlapsBooked = React.useCallback(
    (from: Date, to: Date) => {
      if (bookedNights.size === 0) return false;
      const start = from < to ? from : to;
      const end = from < to ? to : from;
      // Check every night in [start, end - 1]
      const lastNight = addDays(end, -1);
      if (lastNight < start) return isDateBooked(start);
      for (const d of eachDayOfInterval({ start, end: lastNight })) {
        if (isDateBooked(d)) return true;
      }
      return false;
    },
    [bookedNights, isDateBooked]
  );

  // Calculate days between dates - INCLUSIVE of both start and end dates (for fishing permits)
  const calculateDays = (start: Date, end: Date) => {
    // Normalize dates to remove time component for consistent comparison
    const normalizedStart = new Date(start);
    normalizedStart.setHours(0, 0, 0, 0);
    
    const normalizedEnd = new Date(end);
    normalizedEnd.setHours(0, 0, 0, 0);
    
    // Add 1 because we want to include both the start and end dates
    const days = differenceInDays(normalizedEnd, normalizedStart) + 1;
    console.log(`DateRangePicker calculateDays: ${normalizedStart.toDateString()} to ${normalizedEnd.toDateString()} = ${days} days (inclusive)`);
    return days;
  };
  
  // Calculate nights between dates - EXCLUSIVE of end date (for camping)
  const calculateNights = (start: Date, end: Date) => {
    // Normalize dates to remove time component for consistent comparison
    const normalizedStart = new Date(start);
    normalizedStart.setHours(0, 0, 0, 0);
    
    const normalizedEnd = new Date(end);
    normalizedEnd.setHours(0, 0, 0, 0);
    
    // Nights = gaps between days (hotel-style: arrive Apr 10, depart Apr 12 = 2 nights)
    const nights = differenceInDays(normalizedEnd, normalizedStart);
    console.log(`DateRangePicker calculateNights: ${normalizedStart.toDateString()} to ${normalizedEnd.toDateString()} = ${nights} nights (exclusive)`);
    return nights;
  };

  // Format the date range display
  const formatDateRange = () => {
    if (startDate && endDate) {
      // Normalize dates for accurate comparison (remove time component)
      const normalizedStartDate = new Date(startDate);
      normalizedStartDate.setHours(0, 0, 0, 0);
      
      const normalizedEndDate = new Date(endDate);
      normalizedEndDate.setHours(0, 0, 0, 0);
      
      console.log(`DateRangePicker - Normalized dates: ${normalizedStartDate.toISOString()} to ${normalizedEndDate.toISOString()}, displayUnit: ${displayUnit}`);
      
      // If start and end dates are the same, just show a single date
      if (normalizedStartDate.getTime() === normalizedEndDate.getTime()) {
        return showRangeCount
          ? `${format(normalizedStartDate, "MMM d, yyyy")} (1 ${displayUnit === "days" ? "day" : "night"})`
          : format(normalizedStartDate, "MMM d, yyyy");
      }
      
      if (!showRangeCount) {
        return `${format(startDate, "MMM d, yyyy")} - ${format(endDate, "MMM d, yyyy")}`;
      }

      // Calculate based on the display unit (inclusive for days, exclusive for nights)
      const count = displayUnit === "days" 
        ? calculateDays(normalizedStartDate, normalizedEndDate) 
        : calculateNights(normalizedStartDate, normalizedEndDate);
      
      const unitText = displayUnit === "days" ? (count === 1 ? "day" : "days") : (count === 1 ? "night" : "nights");
      
      console.log(`DateRangePicker - Calculated ${displayUnit}: ${count}`);
      
      return `${format(startDate, "MMM d, yyyy")} - ${format(endDate, "MMM d, yyyy")} (${count} ${unitText})`;
    } else if (startDate) {
      return `${format(startDate, "MMM d, yyyy")} - Select end date`;
    } else {
      return placeholder;
    }
  };

  // Reset calendar when dates change externally (like from the Clear Form button)
  React.useEffect(() => {
    if (!startDate || !endDate) {
      setIsCalendarOpen(false);
    }
  }, [startDate, endDate]);

  return (
    <div className={className}>
      <div className="flex flex-col space-y-1 mb-2">
        <span className="text-sm font-medium text-tribal-secondary">
          {startLabel} - {endLabel}
        </span>
      </div>
      <Popover modal={true} open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={"outline"}
            className={cn(
              "w-full justify-start text-left font-normal",
              !startDate && !endDate && "text-muted-foreground"
            )}
            disabled={disabled}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {formatDateRange()}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0 max-w-[calc(100vw-2rem)] max-h-[80vh] overflow-y-auto"
          align="start"
        >
          {stayPresets && stayPresets.length > 0 && (
            <div className="px-3 pt-3 pb-2 border-b border-border bg-muted/30 sticky top-0 z-10">
              <div className="text-xs text-muted-foreground mb-2">
                {startDate
                  ? "Quick stay length (sets departure):"
                  : "Pick an arrival date below, then choose a length:"}
              </div>
              <div className="flex flex-wrap gap-2">
                {stayPresets.map((p) => {
                  const isActive =
                    !!startDate &&
                    !!endDate &&
                    differenceInDays(endDate, startDate) === p.nights;
                  return (
                    <Button
                      key={p.label}
                      type="button"
                      variant={isActive ? "default" : "outline"}
                      size="sm"
                      disabled={!startDate}
                      onClick={() => {
                        if (!startDate) return;
                        const departure = addDays(startDate, p.nights);
                        if (rangeOverlapsBooked(startDate, departure)) {
                          onInvalidRange?.(
                            `A ${p.label.toLowerCase()} stay from this arrival overlaps an existing reservation. Pick a different arrival date.`
                          );
                          return;
                        }
                        setEndDate(departure);
                      }}
                    >
                      {p.label}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
          <Calendar
            mode="range"
            fixedWeeks
            defaultMonth={startDate || undefined}
            selected={{
              from: startDate || undefined,
              to: endDate || undefined,
            }}
            onSelect={(range) => {
              if (!range?.from) {
                setStartDate(undefined);
                setEndDate(undefined);
                return;
              }

              // Single date click (no end yet)
              if (!range.to || isSameDay(range.from, range.to)) {
                if (isDateBooked(range.from)) {
                  onInvalidRange?.("That date is already reserved.");
                  return;
                }
                setStartDate(range.from);
                setEndDate(range.from);
                return;
              }

              // Full range — verify no booked night sits inside it
              if (rangeOverlapsBooked(range.from, range.to)) {
                onInvalidRange?.(
                  "Selected range overlaps an existing reservation. Please pick dates that don't cross a booked period."
                );
                // Keep the start, clear the end so user can pick again
                setStartDate(range.from);
                setEndDate(range.from);
                return;
              }

              setStartDate(range.from);
              setEndDate(range.to);
            }}
            initialFocus
            className={cn("p-3 pointer-events-auto")}
            numberOfMonths={numberOfMonths}
            modifiers={{ booked: (date) => isDateBooked(date) }}
            modifiersStyles={{
              booked: {
                backgroundColor: "#fed7aa",
                color: "#c2410c",
                textDecoration: "line-through",
              },
            }}
            disabled={[
              ...(disablePastDates ? [{ before: today }] : []),
              (date: Date) => isDateBooked(date),
            ]}
          />
          {(bookings.length > 0 || bookingsLoading) && (
            <div className="px-3 pt-2 pb-1 text-xs text-muted-foreground flex items-center gap-2 border-t border-border">
              <span
                className="inline-block w-3 h-3 rounded-sm"
                style={{ backgroundColor: "#fed7aa", border: "1px solid #c2410c" }}
              />
              <span>
                {bookingsLoading
                  ? "Loading availability…"
                  : "Booked / unavailable"}
              </span>
            </div>
          )}
          <div className="p-3 border-t border-border flex items-center justify-between sticky bottom-0 bg-background z-10">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setStartDate(undefined);
                setEndDate(undefined);
                setIsCalendarOpen(false);
              }}
            >
              Clear
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setIsCalendarOpen(false);
              }}
            >
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
